// App.js
import React, { useState, useEffect, useRef, useCallback } from "react";
import { Routes, Route, useNavigate, Navigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";

import PageContent from "./components/Layout/PageContent/PageContent.component";
import SignIn, { PasswordReset } from "./components/SignIn/SignIn.component";
import PopupManager from "./components/Layout/PopUpManager/PopupManager.component.jsx";
import Layout from "./components/Layout/TheLayout/Layout.component.jsx";

import { PlayerProvider } from "./Contexts/playerData.context.jsx";
import { NavBarProvider } from "./Contexts/navbar.items.context.jsx";
import { DataLayerProvider, useDataLayer } from "./Contexts/DataLayer.context.jsx";
import { AllThingsListsProvider, useAllThingsLists } from "./Contexts/allThingsList.context.jsx";
import { BoxProvider } from "./Contexts/boxData.context.jsx";
import { MaskProvider } from "./Contexts/mask.context";
import GlobalMask from "./components/GlobalMask";

import {
    getMyList,
    getAllSchools,
    getMyScouts,
    saveAuthUser,
    clearAuthUser,
    getStoredUserToken,
} from "./utils/functions.ts";

const BB_LEAGUE_STORAGE_KEY = "BBLeague";
const BB_DATE_STORAGE_KEY = "BBDate";
const BB_NOTE_STORAGE_KEY = "FBX_BLUE_TRANSACTION_NOTE_DRAFT";

const hasBluBarsAccess = (user) => {
    const securityLevel = String(user?.userContactLevel ?? user?.security ?? user?.ADMIN ?? user?.admin ?? "").trim();
    return securityLevel === "1" || user?.ADMIN === true || user?.admin === true;
};

const clearBluBarsSessionPrefs = (user) => {
    if (user && !hasBluBarsAccess(user)) return;
    try {
        localStorage.removeItem(BB_LEAGUE_STORAGE_KEY);
        localStorage.removeItem(BB_DATE_STORAGE_KEY);
        localStorage.removeItem(BB_NOTE_STORAGE_KEY);
    } catch {}
};

const AppShell = ({ onLogout }) => (
    <PlayerProvider>
        <BoxProvider>
            <NavBarProvider>
                <PopupManager onLogout={onLogout} />
                <Layout onLogout={onLogout} />
            </NavBarProvider>
        </BoxProvider>
    </PlayerProvider>
);

const DashboardLoadingBar = () => {
    return (
        <div
            className="position-fixed top-0 start-0 w-100 text-center py-2 bg-light border-bottom"
            style={{ zIndex: 99999 }}
        >
            <span className="spinner-border spinner-border-sm text-primary me-2" role="status" />
            <span className="text-muted fw-semibold">Loading data....</span>
        </div>
    );
};

const AppContent = () => {
    const navigate = useNavigate();
    const bootRanRef = useRef(false);

    const {
        updateSchoolArray,
        updateListArray,
        updateSavedQueryArray,
        updateSharedListArray,
        updateScoutArray,
        updateShortPositionArray,
    } = useDataLayer();

    const { updateQuickList, updateQuickListNumbers } = useAllThingsLists();

    const [isSignedIn, setIsSignedIn] = useState(false);
    const [loadingData, setLoadingData] = useState(false);
    const [bootChecked, setBootChecked] = useState(false);

    const decodeValidToken = () => {
        const token = getStoredUserToken();
        if (!token) return null;

        try {
            const decoded = jwtDecode(token);
            const now = Math.floor(Date.now() / 1000);

            if (
                decoded?.exp !== undefined &&
                !Number.isNaN(Number(decoded.exp)) &&
                Number(decoded.exp) < now
            ) {
                clearAuthUser();
                return null;
            }

            return decoded;
        } catch (error) {
            console.error("Token decode failed:", error);
            clearAuthUser();
            return null;
        }
    };

    const loadShortPositionData = async () => {
        try {
            const response = await fetch("https://www.fbxchange.com/ReactPHP/loadData.php", {
                method: "POST",
                headers: { "Content-Type": "application/x-www-form-urlencoded" },
                body: new URLSearchParams({
                    method: "getShortPositionData",
                }).toString(),
            });

            const shortPositions = await response.json().catch(() => []);
            updateShortPositionArray(Array.isArray(shortPositions) ? shortPositions : []);
        } catch (error) {
            console.error("Failed to load short positions:", error);
            updateShortPositionArray([]);
        }
    };

    const bootUserData = async (userData) => {
        const scout = userData?.scout ?? userData?.userId ?? "";
        const team = userData?.team ?? "";

        try {
            const [listsObj, schools, scouts] = await Promise.all([
                getMyList(scout, team),
                getAllSchools(),
                getMyScouts(scout, team),
            ]);

            setLoadingData(false);

            const myListArr = Array.isArray(listsObj?.myList) ? listsObj.myList : [];
            const quickListItem = myListArr.find((item) => item?.listName === "-QUICK LIST-");
            const quickListId = String(quickListItem?.listId ?? "");
            const quickListNums = Array.isArray(quickListItem?.theNumbers)
                ? quickListItem.theNumbers.map((num) => String(num))
                : [];

            updateListArray(myListArr);
            updateSavedQueryArray(Array.isArray(listsObj?.savedQuery) ? listsObj.savedQuery : []);
            updateSharedListArray(Array.isArray(listsObj?.sharedList) ? listsObj.sharedList : []);
            updateQuickList(quickListId);
            updateQuickListNumbers(quickListNums);
            updateSchoolArray(Array.isArray(schools) ? schools : []);
            updateScoutArray(Array.isArray(scouts) ? scouts : []);
        } catch (error) {
            console.error("Failed loading list/school/scout boot data:", error);
            updateListArray([]);
            updateSavedQueryArray([]);
            updateSharedListArray([]);
            updateQuickList("");
            updateQuickListNumbers([]);
            updateSchoolArray([]);
            updateScoutArray([]);
        }

        await loadShortPositionData();
    };

    const handleLogout = useCallback(() => {
        clearBluBarsSessionPrefs(decodeValidToken());
        clearAuthUser();
        localStorage.removeItem("FBX_SELECTED_LIST");
        localStorage.removeItem("user2026");

        setIsSignedIn(false);
        setLoadingData(false);
        setBootChecked(true);

        navigate("/login", { replace: true });
    }, [navigate]);

    useEffect(() => {
        const logoutListener = () => {
            handleLogout();
        };

        window.addEventListener("fbx-logout", logoutListener);
        return () => {
            window.removeEventListener("fbx-logout", logoutListener);
        };
    }, [handleLogout]);

    useEffect(() => {
        if (bootRanRef.current) return;
        bootRanRef.current = true;

        let alive = true;

        const runBoot = async () => {
            const userData = decodeValidToken();

            if (!userData) {
                if (alive) {
                    setIsSignedIn(false);
                    setLoadingData(false);
                    setBootChecked(true);
                    if (!window.location.hash.startsWith("#/reset-password")) {
                        navigate("/login", { replace: true });
                    }
                }
                return;
            }

            if (alive) {
                setIsSignedIn(true);
                setBootChecked(true);
                setLoadingData(true);
            }

            bootUserData(userData)
                .catch((error) => {
                    console.error("Background boot failed:", error);
                })
                .finally(() => {
                    if (alive) {
                        setLoadingData(false);
                    }
                });
        };

        runBoot();

        return () => {
            alive = false;
        };
    }, [navigate]);

    const handleSignIn = async ({ username, password }) => {
        try {
            const response = await fetch("https://www.fbxchange.com/ReactPHP/reactSearchNew.php", {
                method: "POST",
                headers: { "Content-Type": "application/x-www-form-urlencoded" },
                body: new URLSearchParams({
                    method: "signIn",
                    user: username,
                    userp: password,
                }).toString(),
            });

            const data = await response.json().catch(() => ({}));
            const token = data?.user || data?.token || "";

            if (!data?.success || !token) {
                alert("Invalid credentials");
                setIsSignedIn(false);
                setLoadingData(false);
                return false;
            }

            saveAuthUser(token);

            const userData = decodeValidToken();
            if (!userData) {
                alert("Your session token could not be read.");
                setIsSignedIn(false);
                setLoadingData(false);
                return false;
            }

            clearBluBarsSessionPrefs(userData);

            console.log("SignIn non-Yes show fields:", userData.showFields || {});

            setIsSignedIn(true);
            setBootChecked(true);
            setLoadingData(true);
            navigate("/dashboard", { replace: true });

            bootUserData(userData)
                .catch((error) => {
                    console.error("Post-login boot failed:", error);
                })
                .finally(() => {
                    setLoadingData(false);
                });
            return true;
        } catch (error) {
            console.error("Sign in failed:", error);
            clearAuthUser();
            setIsSignedIn(false);
            setLoadingData(false);
            alert("Sign in failed.");
            return false;
        }
    };

    if (!bootChecked) {
        return (
            <div
                className="d-flex flex-column justify-content-center align-items-center"
                style={{ minHeight: "100vh", background: "#ffffff" }}
            >
                <div className="spinner-border text-primary mb-3" role="status" />
                <div className="fs-5 fw-semibold text-muted">Loading data....</div>
            </div>
        );
    }

    return (
        <>
            {isSignedIn && loadingData && <DashboardLoadingBar />}

            <Routes>
                <Route
                    path="/login"
                    element={
                        isSignedIn ? (
                            <Navigate to="/dashboard" replace />
                        ) : (
                            <SignIn onSignIn={handleSignIn} />
                        )
                    }
                />

                <Route
                    path="/reset-password"
                    element={
                        isSignedIn ? (
                            <Navigate to="/dashboard" replace />
                        ) : (
                            <PasswordReset />
                        )
                    }
                />

                <Route
                    path="/"
                    element={<Navigate to={isSignedIn ? "/dashboard" : "/login"} replace />}
                />

                <Route
                    path="/dashboard"
                    element={isSignedIn ? <AppShell onLogout={handleLogout} /> : <Navigate to="/login" replace />}
                />

                <Route
                    path="/pagecontent"
                    element={isSignedIn ? <PageContent /> : <Navigate to="/login" replace />}
                />

                <Route
                    path="*"
                    element={<Navigate to={isSignedIn ? "/dashboard" : "/login"} replace />}
                />
            </Routes>
        </>
    );
};

const App = () => {
    return (
        <div className="app-container">
            <MaskProvider>
                <AllThingsListsProvider>
                    <DataLayerProvider>
                        <AppContent />
                    </DataLayerProvider>
                </AllThingsListsProvider>
                <GlobalMask />
            </MaskProvider>
        </div>
    );
};

export default App;
